import { Button, ButtonGroup, Text, useDisclosure } from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import { Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import {
  IPollQueryJob,
  useCreateQueryJob,
  usePollQueryJob,
} from '@investigator/features/investigations/queries';
import { useHumioToken } from '@investigator/shared/providers';
import { useQueryWithRetry } from '@investigator/shared/hooks';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Column } from 'react-table';
import secureJsonParse from 'secure-json-parse';
import { LogscaleDetailsModal } from '../LogscaleDetailsModal';

export interface InvestigateLogsTableProps {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  alertsName: string;
  alertsUid?: string;
  alertsTimestamp: string;
  alertsTenant?: string;
  logScaleQuery?: string;
  investigateLogsTableData?: LogDetails[];
  drawerLoading?: boolean;
}

export interface LogDetails {
  ['#path']: string;
  ['id.orig_h']: string;
  ['id.resp_p']: string;
  local_orig: string;
  local_resp: string;
}

export const InvestigateLogsTableModal = ({
  isOpen,
  onClose,
  onOpen,
  alertsName,
  alertsUid,
  investigateLogsTableData,
  alertsTimestamp,
  alertsTenant,
  logScaleQuery,
  drawerLoading = false,
}: InvestigateLogsTableProps) => {
  const [activeRow, setActiveRow] = useState<Record<string, string>>({});
  const [logscaleType, setLogscaleType] = useState<string>('');
  const { token: sessionToken } = useHumioToken();
  const shouldFetch =
    !investigateLogsTableData || investigateLogsTableData.length === 0;

  const {
    isOpen: logScaleDetailsIsOpen,
    onOpen: logScaleDetailsOnOpen,
    onClose: logScaleDetailsOnClose,
  } = useDisclosure();

  const startTime = moment(alertsTimestamp).subtract(20, 'minutes').unix();
  const endTime = moment(alertsTimestamp).add(20, 'minutes').unix();
  const [logDetailsQueryJobId, setLogDetailsQueryJobId] = useState<string>();
  const [
    logDetailsCreateQueryJob,
    { loading: logDetailsCreateQueryJobLoading },
  ] = useCreateQueryJob({
    variables: {
      clientSessionId: sessionToken || null,
      queryInputJob: {
        queryStringWithoutSortSize: alertsUid
          ? `uid="${alertsUid}"`
          : logScaleQuery,
        start: startTime.toString(),
        end: endTime.toString(),
        tenantsForQuery: [alertsTenant],
      },
      size: 400,
      sort: [
        {
          sort_by: '_write_ts',
          sort_dir: 'asc',
        },
      ],
    },
    onCompleted: (data) => {
      if (data?.createQueryJob?.query_job_id) {
        setLogDetailsQueryJobId(data.createQueryJob.query_job_id);
      }
    },
  });

  const { data, loading: logDetailsLoading } = useQueryWithRetry<
    IPollQueryJob,
    { clientSessionId: string | null; queryJobId: string | undefined }
  >({
    queryHook: usePollQueryJob,
    variables: {
      clientSessionId: sessionToken || null,
      queryJobId: logDetailsQueryJobId,
    },
    skip: !logDetailsQueryJobId,
    onError(error) {
      console.error(error);
      setLogDetailsQueryJobId(undefined);
    },
  });

  const logsData = data?.pollQueryJob?.events?.map((item) =>
    secureJsonParse(item),
  );

  useEffect(() => {
    if (!shouldFetch) return;
    if (
      isOpen &&
      (alertsUid || logScaleQuery) &&
      (alertsTenant?.trim() ?? '') !== ''
    ) {
      setLogDetailsQueryJobId(undefined);
      logDetailsCreateQueryJob();
    }
    if (!isOpen) {
      setLogDetailsQueryJobId(undefined);
    }
    // alertsTenant is intentionally omitted: it's used for validation only to prevent empty tenant queries
  }, [isOpen, alertsUid, logScaleQuery, logDetailsCreateQueryJob, shouldFetch]);

  const dataTableColumns: Column<LogDetails>[] = useMemo(() => {
    return [
      {
        Header: '#path',
        accessor: '#path',
      },
      {
        Header: 'id.orig_h',
        accessor: (row: LogDetails) => row['id.orig_h'],
        Cell: (props: { value: string }) => <Text>{props?.value ?? '-'}</Text>,
      },
      {
        Header: 'id.resp_p',
        accessor: (row: LogDetails) => row['id.resp_p'],
        Cell: (props: { value: string }) => <Text>{props?.value ?? '-'}</Text>,
      },
      {
        Header: 'local_orig',
        accessor: 'local_orig',
        Cell: (props: { value: string }) => <Text>{props?.value ?? '-'}</Text>,
      },
      {
        Header: 'local_resp',
        accessor: 'local_resp',
        Cell: (props: { value: string }) => <Text>{props?.value ?? '-'}</Text>,
      },
    ];
  }, []);

  const handleRowClick = (e: { original: LogDetails }) => {
    setLogscaleType(e.original?.['#path']);
    setActiveRow(e.original as unknown as Record<string, string>);
    logScaleDetailsOnOpen();
    onClose();
  };

  return (
    <>
      {isOpen && (
        <Modal
          size="xl"
          isOpen={isOpen}
          onClose={onClose}
          title={`${alertsName} | ${alertsTimestamp}`}
          body={
            (shouldFetch &&
              (logDetailsCreateQueryJobLoading || logDetailsLoading)) ||
            drawerLoading ? (
              <GraphQLReqStatus
                loading={
                  logDetailsCreateQueryJobLoading ||
                  logDetailsLoading ||
                  drawerLoading
                }
              />
            ) : (
              <DataTable
                data={investigateLogsTableData || logsData}
                columns={dataTableColumns}
                isLined={true}
                autoResetPage={false}
                autoResetSortBy={false}
                autoResetRowSelection={false}
                onRowClick={handleRowClick}
              />
            )
          }
          footer={
            <ButtonGroup>
              <Button variant="solid" colorScheme="gray" onClick={onClose}>
                Close
              </Button>
            </ButtonGroup>
          }
        />
      )}
      {logScaleDetailsIsOpen && (
        <LogscaleDetailsModal
          alertsName={alertsName}
          alertsTimestamp={alertsTimestamp}
          isModalOpen={logScaleDetailsIsOpen}
          onModalClose={logScaleDetailsOnClose}
          onOpen={onOpen}
          data={activeRow}
          title={`${logscaleType ? logscaleType.toUpperCase() : 'Log'} Details`}
          showBackToLogFlag={true}
        />
      )}
    </>
  );
};
